// src/utils/Onboarding/hairgenApi.js

/**
 * HAIRGEN API
 * 
 * Firebase function client for hair generation.
 * Extracted into a shared utility so it can be called from anywhere.
 */

const GENERATE_HAIR_IMAGE_BASE_URL = 'https://europe-west3-match-medic-p0.cloudfunctions.net/generateHairImage';

/**
 * Trigger generateHairImage HTTPS function for a given storage fileName.
 * Returns a promise that resolves with the JSON result.
 * 
 * @param {Object} options
 * @param {string} options.fileName - Firebase Storage path of the input image
 * @param {Object} [options.reportMetadata] - Optional metadata for report viewing feature
 * @param {string} [options.reportMetadata.userName] - User's name for UI personalization
 * @param {number} [options.reportMetadata.userAge] - User's age for score calculations
 * @param {number} [options.reportMetadata.candidateScorePercent] - Pre-computed candidate score
 * @param {number} [options.reportMetadata.expectedSatisfactionPercent] - Pre-computed satisfaction score
 * @param {string} [options.reportMetadata.analysisRequestId] - Links generation to analysis report
 * @returns {Promise<Object>} API response with generated image URL
 * 
 * @example
 * const result = await triggerHairGeneration({ 
 *   fileName: 'image_analysis_test/123_abc.jpg',
 *   reportMetadata: { analysisRequestId: 'req_123', userName: 'John' }
 * });
 * const generatedUrl = result?.operation?.output?.firebaseStorageUrl;
 */
export async function triggerHairGeneration({ fileName, reportMetadata }) {
  if (!fileName) throw new Error('fileName is required to trigger hair generation');
  
  const urlPost = `${GENERATE_HAIR_IMAGE_BASE_URL}/generate`;
  
  console.log('[HairgenApi] Triggering hair generation for:', fileName);
  if (reportMetadata) {
    console.log('[HairgenApi] With reportMetadata:', Object.keys(reportMetadata));
  }
  
  // Build request body
  const requestBody = { fileName };
  if (reportMetadata && typeof reportMetadata === 'object') {
    requestBody.reportMetadata = reportMetadata;
  }
  
  try {
    const resp = await fetch(urlPost, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      mode: 'cors',
      body: JSON.stringify(requestBody),
    });
    
    const text = await resp.text();
    console.log('[HairgenApi] Raw response length:', text.length);
    
    let json;
    try { json = JSON.parse(text); } catch { json = { raw: text }; }
    
    if (!resp.ok) {
      console.error('[HairgenApi] HTTP error:', { status: resp.status, body: json });
      throw new Error(json?.error || `Function HTTP ${resp.status}`);
    }
    
    if (json?.success === false) {
      console.error('[HairgenApi] Function returned error:', json);
      throw new Error(json?.error || 'Hair generation failed');
    }
    
    // Debug: Log full response structure to understand what we're getting
    console.log('[HairgenApi] SUCCESS - Full response structure:', {
      topLevelKeys: Object.keys(json || {}),
      hasOperation: !!json?.operation,
      operationKeys: json?.operation ? Object.keys(json.operation) : null,
      hasOutput: !!json?.output,
      outputKeys: json?.output ? Object.keys(json.output) : null,
      hasOperationOutput: !!json?.operation?.output,
      operationOutputKeys: json?.operation?.output ? Object.keys(json.operation.output) : null,
      // Direct paths
      directOutputUrl: json?.output?.firebaseStorageUrl ? 'PRESENT' : 'MISSING',
      operationOutputUrl: json?.operation?.output?.firebaseStorageUrl ? 'PRESENT' : 'MISSING',
      requestId: json?.requestId,
      processingTime: json?.processingTime || json?.operation?.processingTime,
    });
    
    console.log('[HairgenApi] Success, processing time:', json?.operation?.processingTime, 'ms');
    
    return json;
  } catch (err) {
    console.error('[HairgenApi] Hair generation request failed:', err);
    throw err;
  }
}

/**
 * Extract the generated image URL from the API response.
 * 
 * @param {Object} result - API response from triggerHairGeneration
 * @returns {string | null} Firebase Storage URL of generated image
 */
export function getGeneratedImageUrl(result) {
  return result?.operation?.output?.firebaseStorageUrl || null;
}

/**
 * Extract processing time from the API response.
 * 
 * @param {Object} result - API response from triggerHairGeneration
 * @returns {number | null} Processing time in milliseconds
 */
export function getProcessingTime(result) {
  return result?.operation?.processingTime || null;
}

/**
 * Check if the API response indicates success with a valid generated image.
 * 
 * @param {Object} result - API response from triggerHairGeneration
 * @returns {boolean} True if successful with valid output
 */
export function isSuccessfulResult(result) {
  return result?.success === true && !!getGeneratedImageUrl(result);
}
